<!-- Hero Section with Background Image -->
<div class="trainers-hero">
  <div class="trainers-hero-bg"></div>
  <div class="trainers-hero-overlay"></div>
  <div class="container-xxl py-10 position-relative">
    <!-- Page Header -->
    <div class="d-flex flex-wrap flex-stack mb-10">
      <h1 class="fw-bold my-2 text-white"><i class="fa fa-user-graduate me-3" style="color: #9b59b6;"></i>{{ 'trainers.title' | translate }}</h1>
    </div>

    <!-- Filters Section -->
    <div class="card mb-0 shadow-lg">
      <div class="card-body">
        <div class="row g-5 align-items-end">
          <!-- Search Filter -->
          <div class="col-md-4">
            <app-input-component
              [type]="'text'"
              [(value)]="searchQuery"
              [label]="'trainers.search' | translate"
              [placeholder]="'trainers.searchPlaceholder' | translate"
              [floating]="true"
              (valueChange)="onSearchChange()"
            />
          </div>

          <!-- Specialization Filter -->
          <div class="col-md-3">
            <app-selectorini
              [options]="specializations"
              [displayKey]="'label'"
              [valueKey]="'value'"
              [(value)]="selectedSpecialization"
              [label]="'trainers.specialization' | translate"
              [placeholder]="'trainers.allSpecializations' | translate"
              [floating]="true"
              (valueChange)="onSpecializationChange()"
            />
          </div>

          <!-- Sort by Nearest Button -->
          <div class="col-md-3">
            <button
              class="btn w-100"
              [class.btn-primary]="sortByDistance"
              [class.btn-light]="!sortByDistance"
              (click)="toggleSortByDistance()"
              [disabled]="!userLocation"
              style="height: 50px;"
              [title]="userLocation ? ('competitions.sortByNearest' | translate) : ('competitions.waitingForLocation' | translate)"
            >
              @if (locationLoading) {
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
              } @else {
                <i class="fa fa-location-crosshairs me-2"></i>
              }
              {{ 'competitions.nearest' | translate }}
            </button>
          </div>

          <!-- Clear Filters Button -->
          <div class="col-md-2">
            <app-button
              [variant]="'neutral'"
              [iconClass]="'fa fa-times'"
              [label]="'competitions.clear' | translate"
              [width]="'100%'"
              (clicked)="clearFilters()"
              [title]="'competitions.clearFilters' | translate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="container-xxl py-10">

  <!-- Recommend Trainer Card -->
  <div class="recommend-card mb-8" (click)="openRecommendModal()">
    <div class="d-flex align-items-center cursor-pointer">
      <div class="recommend-icon me-3">
        <i class="fa fa-user-friends text-success"></i>
      </div>
      <div class="flex-grow-1">
        <h6 class="mb-1">{{ 'trainer.recommendTrainer' | translate }}</h6>
        <p class="text-muted mb-0 fs-7">{{ 'trainer.recommendDescription' | translate }}</p>
      </div>
      <i class="fa fa-chevron-right text-muted"></i>
    </div>
  </div>

  <!-- Map Section -->
  @if (!loading && filteredTrainers.length > 0) {
    <div class="mb-10">
      <app-location-map
        [markers]="mapMarkers"
        [config]="mapConfig"
        [loading]="geocodingInProgress"
        (markerClick)="onMapMarkerClick($event)"
        (infoWindowAction)="onMapInfoWindowAction($event)"
      />
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="text-center py-20">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'trainers.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'trainers.loading' | translate }}</p>
    </div>
  }

  <!-- No Results -->
  @if (!loading && filteredTrainers.length === 0) {
    <div class="card">
      <div class="card-body text-center py-20">
        <i class="fa fa-chalkboard-teacher fs-3x text-muted mb-5"></i>
        <h3 class="text-muted mb-3">{{ 'trainers.noResults' | translate }}</h3>
        <p class="text-muted">
          @if (searchQuery || selectedSpecialization) {
            {{ 'trainers.noResultsHintFilters' | translate }}
          } @else {
            {{ 'trainers.noResultsHintEmpty' | translate }}
          }
        </p>
      </div>
    </div>
  }

  <!-- Trainers Grid -->
  @if (!loading && filteredTrainers.length > 0) {
    <div class="trainers-grid">
      @for (trainer of filteredTrainers; track trainer.user_id) {
        <div class="trainer-card" [id]="'trainer-card-' + trainer.user_id" (click)="viewTrainer(trainer)">
          <!-- Profile Image -->
          <div class="trainer-image-container">
            @if (trainer.profile_image_url) {
              <img [src]="trainer.profile_image_url" alt="{{ trainer.full_name }}" class="trainer-image" />
            } @else {
              <div class="trainer-initials">{{ getInitials(trainer) }}</div>
            }
          </div>

          <!-- Distance Badge -->
          @if (userLocation && getTrainerDistance(trainer) !== null) {
            <div class="trainer-distance-badge">
              <i class="fa fa-location-arrow me-1"></i>
              {{ formatDistance(getTrainerDistance(trainer)) }}
            </div>
          }

          <!-- Trainer Info -->
          <div class="trainer-info">
            <h4 class="trainer-name">{{ trainer.full_name }}</h4>

            @if (getLocation(trainer)) {
              <div class="trainer-location">
                <i class="fa fa-map-marker-alt me-2"></i>
                {{ getLocation(trainer) }}
              </div>
            }

            @if (trainer.trainer_experience) {
              <div class="trainer-experience">
                <i class="fa fa-clock me-2"></i>
                {{ trainer.trainer_experience }}
              </div>
            }

            <!-- Specializations -->
            @if (getSpecializationsList(trainer).length > 0) {
              <div class="trainer-specializations">
                @for (spec of getSpecializationsList(trainer).slice(0, 3); track spec) {
                  <span class="specialization-badge">{{ translateSpecialization(spec) }}</span>
                }
                @if (getSpecializationsList(trainer).length > 3) {
                  <span class="specialization-badge more">+{{ getSpecializationsList(trainer).length - 3 }}</span>
                }
              </div>
            }
          </div>

          <!-- View Button -->
          <div class="trainer-action">
            <button class="btn btn-sm btn-light-primary">
              {{ 'trainers.viewProfile' | translate }}
              <i class="fa fa-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      }
    </div>

    <!-- Pagination -->
    @if (totalPages > 1) {
      <div class="d-flex justify-content-center mt-10">
        <nav>
          <ul class="pagination">
            <li class="page-item" [class.disabled]="currentPage === 1">
              <a class="page-link" (click)="previousPage()">
                <i class="fa fa-chevron-left"></i>
              </a>
            </li>
            @for (page of [].constructor(totalPages); track $index; let i = $index) {
              <li class="page-item" [class.active]="currentPage === i + 1">
                <a class="page-link" (click)="goToPage(i + 1)">{{ i + 1 }}</a>
              </li>
            }
            <li class="page-item" [class.disabled]="currentPage === totalPages">
              <a class="page-link" (click)="nextPage()">
                <i class="fa fa-chevron-right"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    }
  }
</div>

<!-- Recommendation Modal -->
@if (showRecommendModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa fa-user-friends me-2 text-success"></i>
            {{ 'trainer.recommendTitle' | translate }}
          </h5>
          <button type="button" class="btn-close" (click)="closeRecommendModal()"></button>
        </div>
        <div class="modal-body">
          <!-- Trainer Name -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.trainerName' | translate }} <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="recommendForm.trainer_name"
              [placeholder]="'trainer.trainerNamePlaceholder' | translate"
            />
          </div>

          <!-- Why Recommend -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.whyRecommend' | translate }} <span class="text-danger">*</span></label>
            <textarea
              class="form-control"
              rows="4"
              [(ngModel)]="recommendForm.description"
              [placeholder]="'trainer.whyRecommendPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Specializations -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.specializations' | translate }}</label>
            <div class="specializations-selector mb-2">
              @for (spec of availableSpecializations; track spec) {
                <button
                  type="button"
                  class="specialization-btn"
                  [class.active]="isRecommendSpecializationSelected(spec)"
                  (click)="toggleRecommendSpecialization(spec)"
                >
                  {{ translateSpecialization(spec) }}
                </button>
              }
            </div>
          </div>

          <!-- Contact Info -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.contactInfo' | translate }}</label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="recommendForm.contact_info"
              [placeholder]="'trainer.contactInfoPlaceholder' | translate"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" (click)="closeRecommendModal()">
            {{ 'common.cancel' | translate }}
          </button>
          <button type="button" class="btn btn-success" (click)="submitRecommendation()" [disabled]="submittingRecommendation">
            @if (submittingRecommendation) {
              <span class="spinner-border spinner-border-sm me-2"></span>
            }
            {{ 'trainer.submitRecommendation' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
}
